import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useModal } from "@/components/essex/ModalContext";

const packages = [
  {
    badge: "COMPLETE PACKAGE",
    badgeStyle: "bg-[#C2A878] text-[#0A0908]",
    name: "The Wedding Video & Photo Collection",
    price: "3,000",
    monthly: "250",
    highlight: true,
    description: "Full day wedding videography and photography in one package. Two videographers and one photographer covering your entire day.",
    features: [
      { text: "Two videographers and one photographer", accent: false },
      { text: "Full day coverage from bridal prep to first dance", accent: false },
      { text: "20 to 30 minute cinematic wedding film", accent: false },
      { text: "Full ceremony edit (multi camera)", accent: false },
      { text: "Full speeches edit (multi camera)", accent: false },
      { text: "Cake cutting and first dance edits", accent: false },
      { text: "Drone footage (weather and location permitting)", accent: false },
      { text: "3 to 4 minute sneak peek within 72 hours", accent: true },
      { text: "30 to 40 sneak peek photos within one week", accent: true },
      { text: "Online gallery with 500+ edited photos", accent: false },
      { text: "Private online delivery. No hidden charges.", accent: false },
    ],
  },
  {
    badge: "MOST POPULAR",
    badgeStyle: "bg-white/15 text-[#F5F5F0]",
    name: "The Wedding Video Collection",
    price: "2,295",
    monthly: "191",
    highlight: false,
    description: "Our most popular wedding videography package. Two videographers, full day, cinematic film, and all the individual edits.",
    features: [
      { text: "Two videographers all day", accent: false },
      { text: "Full day coverage from bridal prep to first dance", accent: false },
      { text: "20 to 30 minute cinematic wedding film", accent: false },
      { text: "Full ceremony edit (multi camera)", accent: false },
      { text: "Full speeches edit (multi camera)", accent: false },
      { text: "Cake cutting and first dance edits", accent: false },
      { text: "Drone footage (weather and location permitting)", accent: false },
      { text: "3 to 4 minute sneak peek within 72 hours", accent: true },
      { text: "Extra videos (singing waiters, etc.) included", accent: false },
      { text: "Private online delivery. No hidden charges.", accent: false },
    ],
  },
  {
    name: "Photo & Highlight Video",
    price: "2,000",
    monthly: "166",
    highlight: false,
    description: "Wedding photography and a highlight film. One videographer and one photographer for up to 8 hours.",
    features: [
      { text: "One videographer and one photographer", accent: false },
      { text: "Up to 8 hours coverage", accent: false },
      { text: "10 to 15 minute highlight film", accent: false },
      { text: "Full ceremony and speeches edits", accent: false },
      { text: "300 to 500 professionally edited photos", accent: false },
      { text: "Private online delivery. No hidden charges.", accent: false },
    ],
  },
  {
    name: "The Photo Collection",
    price: "1,500",
    monthly: "125",
    highlight: false,
    description: "Dedicated wedding photography with one experienced photographer. Full day or up to 8 hours.",
    features: [
      { text: "One experienced photographer", accent: false },
      { text: "Up to 8 hours coverage", accent: false },
      { text: "Online gallery with 500+ edited photos", accent: false },
      { text: "30 to 40 sneak peek photos within one week", accent: false },
      { text: "Private online delivery. No hidden charges.", accent: false },
    ],
  },
];

export default function PackagesSection() {
  const { openModal } = useModal();

  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="bg-[#0A0908] py-24 md:py-32"
      aria-label="Wedding photography and videography packages and pricing"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Packages
          </p>
          <h2
            data-testid="packages-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0] mb-3"
          >
            Simple pricing. No surprises.
          </h2>
          <p className="text-sm text-[#A19D94] font-sans max-w-xl mx-auto leading-relaxed">
            Every package includes full digital delivery, no travel charges, and interest free monthly payments.
          </p>
        </motion.div>

        {/* Top row: 2 main packages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          {packages.slice(0, 2).map((pkg, i) => (
            <PackageCard key={pkg.name} pkg={pkg} index={i} openModal={openModal} />
          ))}
        </div>

        {/* Bottom row: 2 smaller packages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {packages.slice(2).map((pkg, i) => (
            <PackageCard key={pkg.name} pkg={pkg} index={i + 2} openModal={openModal} />
          ))}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-xs text-[#A19D94]/50 font-sans mt-10"
        >
          Interest free monthly payments available on all packages
        </motion.p>
      </div>
    </section>
  );
}

function PackageCard({ pkg, index, openModal }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`package-card-${index}`}
      className={`rounded-lg p-7 border flex flex-col ${
        pkg.highlight
          ? "bg-[#C2A878]/10 border-[#C2A878]/30"
          : "bg-[#11100F] border-white/[0.06]"
      }`}
    >
      {pkg.badge && (
        <span
          data-testid={`package-badge-${index}`}
          className={`self-start text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4 ${pkg.badgeStyle}`}
        >
          {pkg.badge}
        </span>
      )}

      <h3 className="text-lg font-semibold text-[#F5F5F0] font-sans mb-1">
        {pkg.name}
      </h3>

      {pkg.description && (
        <p className="text-xs text-[#A19D94]/60 font-sans mb-3">{pkg.description}</p>
      )}

      <div className="mb-4">
        <span className="text-3xl font-bold text-[#F5F5F0]">From &pound;{pkg.price}</span>
        <span className="text-sm text-[#A19D94]/60 ml-2">or from &pound;{pkg.monthly}/mo</span>
      </div>

      <ul className="space-y-2.5 mb-7 flex-1">
        {pkg.features.map((feature, j) => (
          <li
            key={j}
            className={`flex items-start gap-2.5 text-sm ${
              feature.accent ? "text-[#C2A878]" : "text-[#A19D94]/80"
            }`}
          >
            <Check size={14} className="mt-0.5 flex-shrink-0 text-[#C2A878]" />
            <span>{feature.text}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={openModal}
        data-testid={`package-cta-${index}`}
        className={`w-full py-3 text-sm font-medium tracking-wider uppercase transition-colors ${
          pkg.highlight
            ? "bg-[#C2A878] text-[#0A0908] hover:bg-[#d4bc92]"
            : "bg-white/[0.06] text-[#F5F5F0] border border-white/10 hover:bg-white/10"
        }`}
      >
        Claim My Date
      </button>
    </motion.div>
  );
}
